import api from './api';

export interface CircleDivision {
  id: number;
  parent_id: number | null;
  circle_number: number;
  name: string;
  designation: string;
  location: string;
  contact_number: string;
  email?: string;
  level_type: 'superintending_engineer' | 'executive_engineer';
  sub_number?: string;
  is_fac: boolean;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
  children?: CircleDivision[];
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

class CirclesDivisionsService {
  private readonly endpoint = '/api/circles-divisions';

  async getAll(): Promise<ApiResponse<CircleDivision[]>> {
    const response = await api.get<ApiResponse<CircleDivision[]>>(this.endpoint);
    return response.data;
  }

  async getAllHierarchical(): Promise<ApiResponse<CircleDivision[]>> {
    const response = await api.get<ApiResponse<CircleDivision[]>>(this.endpoint);
    return response.data;
  }

  async getFlat(): Promise<ApiResponse<CircleDivision[]>> {
    const response = await api.get<ApiResponse<CircleDivision[]>>(`${this.endpoint}/flat`);
    return response.data;
  }

  async getByCircleNumber(circleNumber: number): Promise<ApiResponse<CircleDivision[]>> {
    const response = await api.get<ApiResponse<CircleDivision[]>>(`${this.endpoint}/circle/${circleNumber}`);
    return response.data;
  }

  async getStatistics(): Promise<ApiResponse<{
    totalCircles: number;
    totalSuperintendingEngineers: number;
    totalExecutiveEngineers: number;
    totalFACOfficers: number;
  }>> {
    const response = await api.get(`${this.endpoint}/statistics`);
    return response.data;
  }

  async getById(id: number): Promise<ApiResponse<CircleDivision>> {
    const response = await api.get<ApiResponse<CircleDivision>>(`${this.endpoint}/${id}`);
    return response.data;
  }

  async create(data: Partial<CircleDivision>): Promise<ApiResponse<CircleDivision>> {
    const response = await api.post<ApiResponse<CircleDivision>>(this.endpoint, data);
    return response.data;
  }

  async update(id: number, data: Partial<CircleDivision>): Promise<ApiResponse<CircleDivision>> {
    const response = await api.put<ApiResponse<CircleDivision>>(`${this.endpoint}/${id}`, data);
    return response.data;
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${this.endpoint}/${id}`);
    return response.data;
  }

  async toggleStatus(id: number): Promise<ApiResponse<CircleDivision>> {
    const response = await api.put<ApiResponse<CircleDivision>>(`${this.endpoint}/${id}/toggle-status`);
    return response.data;
  }

  async search(searchTerm: string): Promise<ApiResponse<CircleDivision[]>> {
    const response = await api.get<ApiResponse<CircleDivision[]>>(this.endpoint, {
      params: { search: searchTerm }
    });
    return response.data;
  }
}

export const circlesDivisionsService = new CirclesDivisionsService();